import { Alert, Paper, Stack, TextInput, Textarea } from "@mantine/core"
import { useForm } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { forwardRef, useEffect, useImperativeHandle } from "react"
import { useTranslation } from "react-i18next"
import { getErrorMessage } from "../utils"
import { useCreatePermission, useUpdatePermission } from "../hooks/usePermissions"
import type { PermissionFormValues, PermissionTreeNode } from "../models/permission"

interface Props {
  permission?: PermissionTreeNode | null
  onSuccess: () => void
}

const PermissionFormContent = forwardRef<{ submit: () => void }, Props>(
  ({ permission, onSuccess }, ref) => {
    const { t } = useTranslation()
    const isEdit = !!permission && !permission.children
    const createMutation = useCreatePermission()
    const updateMutation = useUpdatePermission()

    const form = useForm<PermissionFormValues>({
      initialValues: {
        name: "",
        description: "",
        rule_name: null,
      },
      validate: {
        name: (v) => (v?.length >= 1 ? null : t("adminPermissions.validation.nameRequired")),
      },
    })

    useEffect(() => {
      if (permission && !permission.children) {
        form.setValues({
          name: permission.name,
          description: permission.description ?? "",
          rule_name: permission.rule_name ?? null,
        })
      }
    }, [permission])

    const handleSubmit = (values: PermissionFormValues) => {
      if (isEdit && permission) {
        updateMutation.mutate(
          { name: permission.name, values },
          {
            onSuccess: () => {
              notifications.show({
                color: "green",
                title: t("common.success"),
                message: t("adminPermissions.updateSuccess"),
              })
              onSuccess()
            },
            onError: (err) => {
              notifications.show({
                color: "red",
                title: t("common.error"),
                message: getErrorMessage(err),
              })
            },
          },
        )
      } else {
        createMutation.mutate(values, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminPermissions.createSuccess"),
            })
            onSuccess()
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      }
    }

    const isPending = createMutation.isPending || updateMutation.isPending
    const handleFormSubmit = form.onSubmit(handleSubmit)
    useImperativeHandle(ref, () => ({ submit: () => handleFormSubmit() }))

    return (
      <form onSubmit={handleFormSubmit}>
        {(createMutation.isError || updateMutation.isError) && (
          <Alert
            color="red"
            mb="md"
            withCloseButton
            onClose={() => {
              createMutation.reset()
              updateMutation.reset()
            }}
          >
            {createMutation.error?.message || updateMutation.error?.message}
          </Alert>
        )}
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            <TextInput
              label={t("adminPermissions.name")}
              {...form.getInputProps("name")}
              disabled={isEdit}
              autoFocus={!isEdit}
            />
            <Textarea
              label={t("adminPermissions.description")}
              placeholder={t("adminPermissions.descriptionPlaceholder")}
              autosize
              minRows={2}
              maxRows={4}
              {...form.getInputProps("description")}
            />
            <TextInput
              label={t("adminPermissions.ruleName")}
              placeholder={t("adminPermissions.ruleNamePlaceholder")}
              {...form.getInputProps("rule_name")}
            />
          </Stack>
        </Paper>
      </form>
    )
  },
)

export default PermissionFormContent
